import { useEffect, useState } from 'react';
import {
  PERMISSION_KEYS,
  type PermissionKey,
  type PermissionsMap,
  type Scope,
} from '@otter/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EditIcon } from '../../components/Icons';
import { api } from '../../lib/api';

type Props = {
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type RoleRow = {
  id: string;
  slug: string;
  name: string;
  isSystem: boolean;
  isEditable: boolean;
  scope: Scope;
  permissions: PermissionsMap;
  usersCount: number;
};

type RolesResponse = {
  items: RoleRow[];
  permissionKeys: PermissionKey[];
};

const PERMISSION_GROUPS: Array<{ title: string; keys: PermissionKey[] }> = [
  {
    title: 'Operativa',
    keys: [
      'dashboard.view',
      'clients.view',
      'clients.edit',
      'invoicesIssued.view',
      'invoicesIssued.edit',
      'invoicesReceived.view',
      'invoicesReceived.edit',
    ],
  },
  {
    title: 'Finanzas',
    keys: ['treasury.view', 'treasury.edit', 'reports.view', 'taxes.view', 'taxes.edit'],
  },
  {
    title: 'Comercial',
    keys: ['pricing.view'],
  },
  {
    title: 'Firmas',
    keys: ['signatures.view', 'signatures.manage'],
  },
  {
    title: 'Administración',
    keys: [
      'admin.users.manage',
      'admin.roles.manage',
      'admin.settings.manage',
      'admin.issuer.manage',
      'admin.series.manage',
    ],
  },
];

const PERMISSION_LABELS: Record<PermissionKey, string> = {
  'dashboard.view': 'Dashboard',
  'clients.view': 'Ver clientes',
  'clients.edit': 'Editar clientes',
  'invoicesIssued.view': 'Ver facturas emitidas',
  'invoicesIssued.edit': 'Editar facturas emitidas',
  'invoicesReceived.view': 'Ver facturas recibidas',
  'invoicesReceived.edit': 'Editar facturas recibidas',
  'treasury.view': 'Ver tesorería',
  'treasury.edit': 'Editar tesorería',
  'reports.view': 'Ver reportes',
  'taxes.view': 'Ver obligaciones fiscales',
  'taxes.edit': 'Editar obligaciones fiscales',
  'pricing.view': 'Ver calculadora de precios',
  'signatures.view': 'Ver firmas electrónicas',
  'signatures.manage': 'Emitir y gestionar firmas',
  'admin.users.manage': 'Gestionar usuarios',
  'admin.roles.manage': 'Gestionar roles',
  'admin.settings.manage': 'Editar ajustes globales',
  'admin.issuer.manage': 'Editar datos del emisor',
  'admin.series.manage': 'Gestionar series de facturación',
};

const SCOPE_LABELS: Record<Scope, string> = {
  own: 'Solo lo propio',
  team: 'Mi equipo (yo + mis directos)',
  all: 'Toda la organización (sin admin)',
};

function emptyPermissions(): PermissionsMap {
  const out: PermissionsMap = {};
  for (const k of PERMISSION_KEYS) out[k] = false;
  return out;
}

export function Roles({ onToast, onError }: Props) {
  const [items, setItems] = useState<RoleRow[]>([]);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api<RolesResponse>('/admin/roles');
      setItems(r.items);
    } catch (e) {
      onError(e);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function remove(id: string) {
    try {
      await api(`/admin/roles/${id}`, { method: 'DELETE' });
      onToast('Rol eliminado', 'ok');
      setDeletingId(null);
      void load();
    } catch (e) {
      onError(e);
    }
  }

  return (
    <div className="adm-section">
      <div className="stitle">Roles</div>
      <h2 style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        Permisos por rol
        <button
          className="btn primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => setCreating(true)}
        >
          <span className="plus">+</span> Nuevo rol
        </button>
      </h2>
      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Rol</th>
              <th>Slug</th>
              <th>Scope</th>
              <th>Usuarios</th>
              <th>Tipo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td><b>{r.name}</b></td>
                <td className="mono">{r.slug}</td>
                <td>{SCOPE_LABELS[r.scope]}</td>
                <td>{r.usersCount}</td>
                <td>
                  {r.isSystem ? (
                    <span className="pill draft">SYSTEM</span>
                  ) : (
                    <span className="pill ok">CUSTOM</span>
                  )}
                </td>
                <td>
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      title={r.isEditable ? 'Editar rol' : 'Rol de sistema (sólo lectura)'}
                      onClick={() => setEditing(r)}
                      disabled={!r.isEditable && r.slug === 'admin'}
                    >
                      <EditIcon />
                    </button>
                    {!r.isSystem && (
                      <button
                        className="btn sm"
                        onClick={() => setDeletingId(r.id)}
                        disabled={r.usersCount > 0}
                        title={
                          r.usersCount > 0
                            ? 'Reasigna los usuarios antes de borrar'
                            : 'Borrar rol'
                        }
                      >
                        borrar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <RoleEditor
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            onToast('Rol creado', 'ok');
            void load();
          }}
          onError={onError}
        />
      )}
      {editing && (
        <RoleEditor
          mode="edit"
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onToast('Rol actualizado', 'ok');
            void load();
          }}
          onError={onError}
        />
      )}
      {deletingId && (
        <ConfirmDialog
          tone="danger"
          title="Borrar rol"
          message="Esta acción no se puede deshacer."
          confirmLabel="Borrar"
          onCancel={() => setDeletingId(null)}
          onConfirm={() => void remove(deletingId)}
        />
      )}
    </div>
  );
}

function RoleEditor(props: {
  mode: 'create' | 'edit';
  role?: RoleRow;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const isEdit = props.mode === 'edit';
  const locked = isEdit && !!props.role && !props.role.isEditable;

  const [name, setName] = useState(props.role?.name ?? '');
  const [slug, setSlug] = useState(props.role?.slug ?? '');
  const [scope, setScope] = useState<Scope>(props.role?.scope ?? 'own');
  const [perms, setPerms] = useState<PermissionsMap>(
    props.role?.permissions ?? emptyPermissions(),
  );
  const [busy, setBusy] = useState(false);

  function togglePerm(key: PermissionKey) {
    setPerms((cur) => ({ ...cur, [key]: !cur[key] }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit && props.role) {
        await api(`/admin/roles/${props.role.id}`, {
          method: 'PATCH',
          body: { name, scope, permissions: perms },
        });
      } else {
        await api('/admin/roles', {
          method: 'POST',
          body: { slug, name, scope, permissions: perms },
        });
      }
      props.onSaved();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form
        className="modal"
        style={{ maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h3>{isEdit ? `Editar rol · ${props.role?.name ?? ''}` : 'Nuevo rol'}</h3>
        {locked && (
          <div className="empty" style={{ marginBottom: 12 }}>
            Este rol es de sistema y no se puede modificar.
          </div>
        )}
        <div className="field">
          <label>Nombre</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required disabled={locked} />
        </div>
        {!isEdit && (
          <div className="field">
            <label>Slug (identificador interno, sin espacios)</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              required
              pattern="^[a-z0-9][a-z0-9-]*$"
            />
          </div>
        )}
        <div className="field">
          <label>Alcance de datos</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
            disabled={locked}
          >
            <option value="own">{SCOPE_LABELS.own}</option>
            <option value="team">{SCOPE_LABELS.team}</option>
            <option value="all">{SCOPE_LABELS.all}</option>
          </select>
        </div>
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>Permisos por sección</label>
          {PERMISSION_GROUPS.map((g) => (
            <div key={g.title} style={{ marginTop: 10 }}>
              <div className="stitle" style={{ fontSize: 11, opacity: 0.7 }}>{g.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {g.keys.map((k) => (
                  <label
                    key={k}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  >
                    <input
                      type="checkbox"
                      checked={perms[k] === true}
                      onChange={() => togglePerm(k)}
                      disabled={locked}
                    />
                    {PERMISSION_LABELS[k]}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cerrar
          </button>
          {!locked && (
            <button type="submit" className="btn primary" disabled={busy}>
              {isEdit ? 'Guardar' : 'Crear rol'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
